import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import { API_ENDPOINTS } from "../config/api";
import { usePost } from "../hooks/useApi";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => {
    return localStorage.getItem("rememberMe") === "true";
  });

  const { post, loading: loginLoading } = usePost(API_ENDPOINTS.LOGIN);
  const initialized = useRef(false);

  // Single initialization effect - runs only once
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const initializeAuth = () => {
      try {
        const savedRememberMe = localStorage.getItem("rememberMe") === "true";
        setRememberMe(savedRememberMe);

        let userData = null;
        let token = null;

        if (savedRememberMe) {
          // Check localStorage for auth data
          const savedUser = localStorage.getItem("adminUser");
          token = localStorage.getItem("token");

          if (savedUser && token) {
            userData = JSON.parse(savedUser);
          }
        } else {
          // Check sessionStorage
          const sessionUser = sessionStorage.getItem("adminUser");
          token = sessionStorage.getItem("token");

          if (sessionUser && token) {
            userData = JSON.parse(sessionUser);
          }
        }

        // Set user if valid data exists
        if (userData && token) {
          setUser(userData);
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
      } finally {
        // Use setTimeout to ensure rendering completes
        setTimeout(() => {
          setLoading(false);
        }, 50);
      }
    };

    initializeAuth();
  }, []);

  // Sync authLoading with loginLoading
  useEffect(() => {
    setAuthLoading(loginLoading);
  }, [loginLoading]);

  // Function to get saved credentials for auto-fill
  const getSavedCredentials = () => {
    if (rememberMe) {
      try {
        const saved = localStorage.getItem("adminCredentials");
        if (saved) {
          return JSON.parse(saved);
        }
      } catch (error) {
        console.error("Error loading saved credentials:", error);
      }
    }
    return null;
  };

  // UPDATED LOGIN FUNCTION - Supports both 2FA and direct login
  const login = async (email, password, shouldRemember = false) => {
    if (!email || !password) {
      return { success: false, error: "Email and password required" };
    }

    try {
      setAuthLoading(true);
      const response = await post({ email, password });

      const payload = response?.data ?? response ?? {};

      // CASE 1: 2FA Required - OTP wala response
      if (payload?.status === true && payload?.user_id) {
        return {
          success: true,
          status: true,
          user_id: payload.user_id,
          message: payload.message,
        };
      }

      // CASE 2: Direct Login - tokens wala response
      const userData = payload?.user ?? payload?.data?.user ?? { email };
      const accessToken =
        payload?.tokens?.access ||
        payload?.data?.tokens?.access ||
        payload?.access ||
        "";

      if (accessToken) {
        // Update remember me state
        setRememberMe(shouldRemember);
        localStorage.setItem("rememberMe", shouldRemember.toString());

        // Save credentials for auto-fill
        if (shouldRemember) {
          localStorage.setItem(
            "adminCredentials",
            JSON.stringify({ email, password }),
          );
        } else {
          localStorage.removeItem("adminCredentials");
        }

        // Clear old auth data first
        localStorage.removeItem("adminUser");
        localStorage.removeItem("token");
        sessionStorage.removeItem("adminUser");
        sessionStorage.removeItem("token");

        // Store auth data based on remember me choice
        if (shouldRemember) {
          localStorage.setItem("adminUser", JSON.stringify(userData));
          localStorage.setItem("token", accessToken);
        } else {
          sessionStorage.setItem("adminUser", JSON.stringify(userData));
          sessionStorage.setItem("token", accessToken);
        }

        // Update user state
        setUser(userData);
        return { success: true, user: userData };
      }

      // CASE 3: Unexpected response
      return {
        success: false,
        error: "Invalid response from server",
      };
    } catch (err) {
      console.error("Login error:", err);
      return {
        success: false,
        error: err.message || "Login failed. Please check credentials.",
      };
    } finally {
      setAuthLoading(false);
    }
  };

  const logout = (clearCredentials = false) => {
    setUser(null);

    localStorage.removeItem("adminUser");
    localStorage.removeItem("token");
    sessionStorage.removeItem("adminUser");
    sessionStorage.removeItem("token");

    if (clearCredentials || !rememberMe) {
      localStorage.removeItem("adminCredentials");
    }
  };

  const updateRememberMe = (shouldRemember) => {
    if (shouldRemember === rememberMe) return;

    setRememberMe(shouldRemember);
    localStorage.setItem("rememberMe", shouldRemember.toString());

    if (!shouldRemember) {
      localStorage.removeItem("adminCredentials");
    }

    if (user) {
      if (shouldRemember) {
        const sessionUser = sessionStorage.getItem("adminUser");
        const sessionToken = sessionStorage.getItem("token");

        if (sessionUser && sessionToken) {
          localStorage.setItem("adminUser", sessionUser);
          localStorage.setItem("token", sessionToken);
          sessionStorage.removeItem("adminUser");
          sessionStorage.removeItem("token");
        }
      } else {
        const localUser = localStorage.getItem("adminUser");
        const localToken = localStorage.getItem("token");

        if (localUser && localToken) {
          sessionStorage.setItem("adminUser", localUser);
          sessionStorage.setItem("token", localToken);
          localStorage.removeItem("adminUser");
          localStorage.removeItem("token");
        }
      }
    }
  };

  const clearSavedCredentials = () => {
    localStorage.removeItem("adminCredentials");
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        loading,
        authLoading,
        rememberMe,
        getSavedCredentials,
        clearSavedCredentials,
        updateRememberMe,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
